import { trpc } from '@/lib/trpc';
import { format, isToday } from 'date-fns';
import type { DateRange } from '../types';

interface UseDashboardHistoryOptions {
  /**
   * Historical/comparison data that never includes today — skips polling
   * entirely and caches longer. Set to true for comparison queries on the
   * Reports page.
   */
  isHistorical?: boolean;
}

/**
 * Hook for fetching historical dashboard statistics.
 *
 * Returns time-series data for the production performance chart.
 * Unlike useDashboard, this does NOT poll continuously since historical data
 * is relatively static. However, if the date range includes today, it will
 * auto-refresh every minute to show current day updates.
 *
 * @param locationId - Location UUID (query disabled if undefined)
 * @param dateRange - Date range for the query { from, to }
 * @param options - Optional behavior overrides
 */
export function useDashboardHistory(
  locationId: string | undefined,
  dateRange: DateRange,
  options?: UseDashboardHistoryOptions
) {
  const isHistorical = options?.isHistorical ?? false;
  // Check if the range includes today (for auto-refresh logic)
  // Uses isToday() for semantic correctness - checks if date is same calendar day
  const includesCurrentDay = !isHistorical && isToday(dateRange.to);

  return trpc.productionOrders.getDashboardHistory.useQuery(
    {
      locationId: locationId!,
      // Calendar dates — interpreted as location-local by the RPC. Using
      // .toISOString() here produced browser-TZ instants that the RPC then
      // reinterpreted in the location TZ, causing single-day picks to span
      // two days when browser and location TZs differed.
      startDate: format(dateRange.from, 'yyyy-MM-dd'),
      endDate: format(dateRange.to, 'yyyy-MM-dd'),
    },
    {
      enabled: !!locationId,
      // Historical comparison data doesn't change — cache aggressively.
      // Otherwise: 1 min if viewing today, 5 min for other past ranges.
      staleTime: isHistorical
        ? 30 * 60 * 1000
        : includesCurrentDay
        ? 60_000
        : 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Auto-refresh every minute if viewing current day, otherwise no polling
      refetchInterval: includesCurrentDay ? 60_000 : false,
    }
  );
}
